import { resolveGrams } from './food-text.js';
const normalize = (text) => text.toLowerCase().replace(/ё/g, 'е');
const solidForm = /сух\p{L}*|порош\p{L}*|кубик\p{L}*|концентрат\p{L}*|сгущ\p{L}*|морожен\p{L}*|взбит\p{L}*|\b(?:powder|dry|dried|cube|concentrate|condensed|evaporated|ice cream|whipped)\b/u;
/** Справочные оценки, не измерение конкретной упаковки.
 * FAO/INFOODS Density Database v2: https://www.fao.org/4/ap815e/ap815e.pdf
 * Для прозрачного бульона используется приближение водного раствора.
 * Неизвестная жидкость НЕ получает универсальную плотность 1.
 */
export function liquidInfo(name) {
    const n = normalize(name);
    if (solidForm.test(n))
        return { liquid: false, densityGPerMl: null };
    // Не путать еду «с молоком / в масле» с самой жидкостью.
    if (/каша|хлопья|хлеб|печенье|шоколад|фарш|котлет|грудк|салат|тунец|сардин|\b(?:bread|cereal|chocolate bar|meat|tuna|salad)\b/u.test(n)) {
        return { liquid: false, densityGPerMl: null };
    }
    if (/молок\p{L}*|\bmilk\b/u.test(n))
        return { liquid: true, densityGPerMl: /кокос|миндал|овсян|соев|козь|\b(?:coconut|almond|oat|soy|goat)\b/u.test(n) ? null : 1.03 };
    if (/кефир|ряженк|айран|йогурт\p{L}*\s+питьев|питьев\p{L}*\s+йогурт|\bkefir\b/u.test(n))
        return { liquid: true, densityGPerMl: null };
    if (/буль[оьй]+н|\b(?:broth|stock)\b/u.test(n))
        return { liquid: true, densityGPerMl: /мисо|\bmiso\b/u.test(n) ? 1.06 : 1.0 };
    if (/суп|борщ|окрошк|\bsoup\b/u.test(n))
        return { liquid: true, densityGPerMl: null };
    if ((/масл\p{L}*/u.test(n) && /оливков|растительн|подсолнечн|рапсов/u.test(n)) || /\b(?:olive|vegetable|sunflower|rapeseed) oil\b/u.test(n)) {
        return { liquid: true, densityGPerMl: 0.92 };
    }
    if (/^(?:вода|воды|water)(?:\s|$)|^(?:чай|чая|кофе|tea|coffee)(?:\s|$)/u.test(n))
        return { liquid: true, densityGPerMl: /сахар|sugar/u.test(n) && !/без\s+сахар|without sugar|unsweetened/u.test(n) ? null : 1.0 };
    // Коктейлям плотность нужна: их заказывают в миллилитрах, а КБЖУ у нас
    // на 100 г. Сладкие смешанные напитки заметно плотнее воды.
    if (/коктейл\p{L}*|пина\s*колад\p{L}*|мохито|дайкири|свимминг\s*пул|зомби|\b(?:cocktail|pina colada|pinacolada|mojito|daiquiri|swimming pool|zombie)\b/u.test(n)) {
        return { liquid: true, densityGPerMl: 1.05 };
    }
    // «Нектар» проверяем как отдельное слово с русскими окончаниями:
    // простая подстрока также совпадала с «нектарин» и превращала фрукт в мл.
    if (/сок\p{L}*|напит\p{L}*|(?:^|[^\p{L}])нектар(?:а|ы|ов|ом|у)?(?:[^\p{L}]|$)|сливк|сироп|уксус|пиво|вино|смузи|\b(?:juice|drink|beverage|cream|syrup|vinegar|beer|wine|smoothie)\b/u.test(n))
        return { liquid: true, densityGPerMl: null };
    return { liquid: false, densityGPerMl: null };
}
export function measurementInfo(info) {
    const inferred = liquidInfo(info.name);
    const density = info.densityGPerMl ?? inferred.densityGPerMl;
    return {
        nutritionBasis: info.nutritionBasis ?? 'g',
        liquid: info.liquid ?? inferred.liquid,
        densityGPerMl: density,
        densityEstimated: density != null && (info.densityEstimated ?? true),
    };
}
export class MeasurementError extends Error {
    statusCode = 422;
}
export function nutritionAmount(item) {
    const amount = item.nutritionBasis === 'ml' ? item.volumeMl : item.grams;
    if (amount == null || !Number.isFinite(amount) || amount <= 0)
        throw new Error('Нет количества для расчёта КБЖУ');
    return amount;
}
/** Возвращает null, когда перевод требует неизвестной плотности. */
export function resolvePortion(fragment, servingSizeG, info) {
    const m = measurementInfo(info);
    let grams = fragment.grams;
    let volumeMl = grams == null ? fragment.volumeMl ?? null : null;
    if ((grams != null && (!Number.isFinite(grams) || grams <= 0 || grams > 5000)) ||
        (volumeMl != null && (!Number.isFinite(volumeMl) || volumeMl <= 0 || volumeMl > 5000)))
        return null;
    if (grams == null && volumeMl == null) {
        if (m.liquid && fragment.count == null && servingSizeG == null)
            volumeMl = 100;
        else
            grams = resolveGrams(fragment, servingSizeG);
    }
    const round = (v) => Math.round(v * 100) / 100;
    if (m.densityGPerMl != null) {
        if (grams == null && volumeMl != null)
            grams = round(volumeMl * m.densityGPerMl);
        if (volumeMl == null && grams != null && m.liquid)
            volumeMl = round(grams / m.densityGPerMl);
    }
    if ((m.nutritionBasis === 'g' && grams == null) || (m.nutritionBasis === 'ml' && volumeMl == null))
        return null;
    return { grams, volumeMl, nutritionBasis: m.nutritionBasis, densityGPerMl: m.densityGPerMl, densityEstimated: m.densityEstimated };
}
export function formatQuantity(item) {
    return item.volumeMl != null ? `${Math.round(item.volumeMl * 10) / 10} мл` : `${item.grams ?? '—'} г`;
}
export function quantityUnit(info) {
    return measurementInfo(info).liquid || info.nutritionBasis === 'ml' ? 'ml' : 'g';
}
//# sourceMappingURL=measurements.js.map